import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { UI } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import { RecordsOpsService } from '../../services/records-ops.service';
import { RoleAccessService } from '../../services/role-access.service';
import { RoleCode } from '../../utils/role-access';
import { formatHumanAmount, formatHumanDateTime } from '../../utils/admin-formatting';
import { TransactionHubComponent } from '../transaction-hub/transaction-hub.component';

function choiceValue(value: unknown): string {
  if (value && typeof value === 'object' && 'value' in value) {
    return String((value as { value?: unknown }).value ?? '');
  }
  return typeof value === 'string' ? value : '';
}

/**
 * Settlement detail: the standard transaction hub, with the back-office payment panel above it for partner and bank
 * settlements waiting to be paid (what to pay, where, by when; mark paid with proof, or fail to return the money).
 */
@Component({
  selector: 'app-settlement-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, TransactionHubComponent, ...UI],
  template: `
    @if (settlement(); as s) {
      @if (needsPayment()) {
        <section class="px-4 pt-3">
          <ui-card class="border-warning-border!" title="Pay this settlement">
            <div header-actions>
              @if (overdue()) {
                <ui-badge variant="danger">overdue</ui-badge>
              } @else if (dueText()) {
                <ui-badge variant="warning">due {{ dueText() }}</ui-badge>
              }
            </div>
            <p class="mb-2 text-sm text-fg">
              Send <strong>{{ amountText() }}</strong> to the destination below, then mark it paid with the
              transfer reference. Reference to quote: <code>{{ s['merchant_reference'] }}</code>
              @if (s['is_international']) {
                <ui-badge class="ms-1" variant="info">international</ui-badge>
              }
            </p>
            <dl class="mb-3 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-12">
              @for (row of destinationRows(); track row.label) {
                <dt class="text-muted sm:col-span-3">{{ row.label }}</dt>
                <dd class="m-0 break-words text-fg sm:col-span-9">{{ row.value }}</dd>
              }
            </dl>

            @if (canPay()) {
              <div class="grid grid-cols-1 gap-x-3 md:grid-cols-3">
                <ui-form-field label="Transfer reference">
                  <input uiInput [(ngModel)]="reference" name="ref" maxlength="100" />
                </ui-form-field>
                <ui-form-field label="Notes">
                  <input uiInput [(ngModel)]="notes" name="notes" maxlength="1000" />
                </ui-form-field>
                <ui-form-field label="Proof (receipt, optional)">
                  <input type="file" uiInput (change)="onProof($event)" />
                </ui-form-field>
              </div>
              <div class="mt-1 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  uiButton
                  class="bg-success! hover:opacity-90"
                  [disabled]="busy()"
                  (click)="markPaid()"
                >
                  Mark as paid
                </button>
                <div class="flex items-center gap-2">
                  <input
                    uiInput
                    class="min-w-64"
                    [(ngModel)]="failReason"
                    name="fail"
                    placeholder="Why can it not be paid?"
                    maxlength="500"
                  />
                  <button
                    type="button"
                    uiButton
                    variant="danger"
                    [disabled]="busy() || !failReason.trim()"
                    (click)="markFailed()"
                  >
                    Fail and refund the wallet
                  </button>
                </div>
              </div>
            } @else {
              <p class="m-0 text-sm text-muted">Only system administrators and finance can pay settlements.</p>
            }
            @if (message()) {
              <ui-alert class="mt-3 block" [variant]="ok() ? 'success' : 'danger'">
                {{ message() }}
              </ui-alert>
            }
          </ui-card>
        </section>
      }
    }
    @if (entityConfig) {
      @for (version of [hubVersion()]; track version) {
        <app-transaction-hub [entityConfig]="entityConfig" [id]="id" />
      }
    }
  `,
})
export class SettlementDetailComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly ops = inject(RecordsOpsService);
  private readonly roles = inject(RoleAccessService);

  readonly settlement = signal<Record<string, unknown> | null>(null);
  readonly busy = signal(false);
  readonly message = signal('');
  readonly ok = signal(false);
  readonly hubVersion = signal(0);
  reference = '';
  notes = '';
  failReason = '';
  private proof: File | null = null;

  readonly canPay = computed(() => {
    const role = this.roles.effectiveRole();
    return role === RoleCode.SYSTEM_ADMIN || role === RoleCode.SYSTEM_FINANCIAL;
  });
  readonly needsPayment = computed(() => {
    const s = this.settlement();
    if (!s) {
      return false;
    }
    const pending = ['PENDING', 'PROCESSING', 'QUEUED'].includes(choiceValue(s['status']).toUpperCase());
    return pending && choiceValue(s['destination_type']) !== 'B2B_INTEGRATION';
  });
  readonly overdue = computed(() => {
    const due = this.settlement()?.['due_at'];
    return typeof due === 'string' && new Date(due).getTime() < Date.now();
  });
  readonly dueText = computed(() => {
    const due = this.settlement()?.['due_at'];
    return typeof due === 'string' ? formatHumanDateTime(due) : '';
  });
  readonly amountText = computed(() => {
    const s = this.settlement();
    return s ? formatHumanAmount(String(s['amount'] ?? ''), String(s['currency_code'] ?? '')) : '';
  });
  readonly destinationRows = computed(() => {
    const s = this.settlement();
    if (!s) {
      return [] as Array<{ label: string; value: string }>;
    }
    const rows: Array<{ label: string; value: string }> = [];
    const add = (label: string, value: unknown): void => {
      const text = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
      if (text) {
        rows.push({ label, value: text });
      }
    };
    add('Beneficiary', s['destination_name']);
    add('Bank / partner', s['destination_bank']);
    add('IBAN', s['iban']);
    add('Account number', s['bank_account_number']);
    add('Routing / sort code', s['bank_routing_number']);
    add('SWIFT / BIC', s['swift_code']);
    add('Bank country', s['bank_country']);
    const details = s['destination_details'];
    if (details && typeof details === 'object') {
      for (const [key, value] of Object.entries(details as Record<string, unknown>)) {
        if (!rows.some((r) => r.value === String(value))) {
          add(key.replace(/_/g, ' '), value);
        }
      }
    }
    if (!rows.some((r) => r.label === 'Account number' || r.label === 'IBAN')) {
      add('Destination', s['destination_account']);
    }
    return rows;
  });

  ngOnInit(): void {
    this.load();
  }

  onProof(event: Event): void {
    this.proof = (event.target as HTMLInputElement).files?.[0] ?? null;
  }

  markPaid(): void {
    this.run(
      this.ops.markSettlementPaid(this.id, {
        external_reference: this.reference.trim(),
        notes: this.notes.trim(),
        proof_file: this.proof,
      }),
    );
  }

  markFailed(): void {
    this.run(this.ops.markSettlementFailed(this.id, this.failReason.trim()));
  }

  private run(call: ReturnType<RecordsOpsService['markSettlementFailed']>): void {
    this.busy.set(true);
    this.message.set('');
    call.subscribe({
      next: (res) => {
        this.busy.set(false);
        this.ok.set(true);
        this.message.set(res.detail);
        this.load();
        this.hubVersion.update((v) => v + 1);
      },
      error: (err: { error?: { detail?: string } }) => {
        this.busy.set(false);
        this.ok.set(false);
        this.message.set(err?.error?.detail ?? 'The action was refused.');
      },
    });
  }

  private load(): void {
    this.ops.getSettlement(this.id).subscribe({ next: (row) => this.settlement.set(row) });
  }
}
